import { Component, type ErrorInfo, type ReactNode } from 'react';

import { LEGAL_URLS, NEW_TAB } from '../lib/brand.js';
import { reportClientError } from '../lib/clientErrors.js';


interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * The last line of defence for the browser.
 *
 * Every page already handles its own failed requests. This catches the other
 * kind — a render that throws — which React answers by unmounting the entire
 * tree. Without a boundary that is a white screen with nothing on it: no
 * explanation, no way back, and nothing the person can tell you afterwards.
 *
 * Deliberately plain. It must not depend on the session, the router, or any
 * data, because any of those could be what just failed.
 */
export class ErrorBoundary extends Component<Props, State> {
  override state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('The interface failed to render', error, info.componentStack);
    /*
     * C-32. The console was the only channel, and nobody reads it. The report
     * carries the path, the message, the stack and whether somebody was signed
     * in, and nothing else; the server tells the operator, at most hourly. The
     * component stack is left out: it names components, not people, but the
     * error's own stack already says where.
     */
    reportClientError(error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    return (
      <div className="flex min-h-full items-center justify-center px-6 py-24">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-fg">
            Something went wrong
          </h1>
          <p className="mt-2 text-sm text-fg-2">
            The page could not be displayed. Nothing you were doing has been lost. Your credits,
            orders and files are unaffected.
          </p>
          <button
            type="button"
            onClick={() => {
              window.location.reload();
            }}
            className="mt-6 rounded-control bg-blue px-4 py-2.5 text-sm font-semibold text-ground transition hover:bg-blue-bright"
          >
            Reload the page
          </button>
          {/* Ruling D-2: the legal line is off every page. This boundary is
              rendered outside the router, so it links to Terms the plain way.

              Straight to the website since WAIT-B round 2, with the shell's
              footer. It matters slightly more here than there: this screen is
              what somebody sees when the app has failed to render, and sending
              them to another portal route to reach the Terms asks the broken
              thing to work twice. `LEGAL_URLS` and `NEW_TAB` are plain string
              constants, so they cost this boundary none of its independence -
              which is why the new-tab props are spread rather than wrapped in
              a shared link component. */}
          <p className="mt-8 text-xs text-fg-faint">
            <a {...NEW_TAB} href={LEGAL_URLS.terms} className="hover:text-fg-2 hover:underline">
              Terms
            </a>
          </p>
        </div>
      </div>
    );
  }
}
